import { useState } from 'react';

import { Button as MuiButton, Stack } from '@mui/material';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { DOCUMENTS } from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  AddButton,
  CardShell,
  ChoiceCards,
  Field,
  Part,
  Parts,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  listedDocuments,
  routeDocuments,
} from '@/james-playground/aa-family-claim/claim/cards/values';

/**
 * Q65, the documents going with the form. What the family uploaded is listed
 * and ticked; what the way the form is signed needs (a registered Power of
 * Attorney, a court order, the SR1) is offered unticked. Anything else can be
 * typed. The ticked list becomes the form's list, one document a line.
 */
const DocumentsCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const id = ask.entries[0];
  const qid = useQids();
  const uploaded = listedDocuments(facts);
  const route = routeDocuments(facts)
    .map((k) => DOCUMENTS.routeDocs[k])
    .filter((d) => !uploaded.includes(d));
  const [ticked, setTicked] = useState<string[]>(uploaded);
  const [extra, setExtra] = useState<string[]>([]);

  const toggle = (doc: string) =>
    setTicked((t) =>
      t.includes(doc) ? t.filter((d) => d !== doc) : [...t, doc],
    );
  const all = [...ticked, ...extra.map((d) => d.trim()).filter(Boolean)];

  const send = (docs: string[]) =>
    onSubmit(
      { [id]: docs.join('\n') },
      docs.length ? DOCUMENTS.reply(docs.length) : DOCUMENTS.noneReply,
    );

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={all.length > 0}
      onContinue={() => send(all)}
      onSkip={onSkip}
    >
      <Parts>
        {uploaded.length > 0 && (
          <Part qid={qid('uploaded')} question={DOCUMENTS.uploaded}>
            <ChoiceCards
              multi
              labelledBy={qid('uploaded')}
              options={uploaded.map((d) => ({ value: d, label: d }))}
              selected={ticked}
              onPick={toggle}
            />
          </Part>
        )}
        {route.length > 0 && (
          <Part qid={qid('route')} question={DOCUMENTS.route}>
            <ChoiceCards
              multi
              labelledBy={qid('route')}
              options={route.map((d) => ({ value: d, label: d }))}
              selected={ticked}
              onPick={toggle}
            />
          </Part>
        )}
        <Stack spacing={1.25}>
          {extra.map((d, i) => (
            <Field
              key={i}
              label={`${DOCUMENTS.addLabel} ${i + 1}`}
              value={d}
              onChange={(e) =>
                setExtra((x) => x.map((y, j) => (j === i ? e.target.value : y)))
              }
            />
          ))}
          <AddButton
            label={DOCUMENTS.add}
            onClick={() => setExtra((x) => [...x, ''])}
          />
        </Stack>
      </Parts>
      {all.length === 0 && (
        <MuiButton variant='outlined' fullWidth onClick={() => send([])}>
          {DOCUMENTS.none}
        </MuiButton>
      )}
    </CardShell>
  );
};

DocumentsCard.displayName = 'DocumentsCard';
export default DocumentsCard;
